import { RECORDER_MIME_PREFERENCE, acceptedMime, type BareAudioMime } from './audioFormat';
import type { QueuedPiece, RecordingState } from './uploadQueue';

/**
 * Per-browser recording in 30 second pieces (h1h2-plan.md section 2).
 *
 * Each piece is a standalone file: every 30 s the current MediaRecorder is
 * stopped and a new one started on the SAME stream the call opened, so there
 * is no second microphone prompt (2.1, 2.2). No timeslice: later timeslice
 * blobs may not decode alone (plan GUESS; the dry run settles it).
 *
 * What is sent is always the bare type of the blob (R-E part 2). A browser
 * with no MediaRecorder, no supported format, or a blob whose bare type is not
 * one of the three is 'not_recorded' from the start, and the bar says so.
 */

export const PIECE_MS = 30_000;
export const PIECE_MAX_BYTES = 512 * 1024;
export const AUDIO_BITS_PER_SECOND = 24_000;
export const LEAVE_WAIT_MS = 10_000;

/** The slice of MediaRecorder the recorder uses, so tests can fake it. */
export interface RecorderLike {
  readonly mimeType: string;
  readonly state: 'inactive' | 'recording' | 'paused';
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

export interface RecorderCtor {
  new (
    stream: MediaStream,
    options: { mimeType: string; audioBitsPerSecond: number },
  ): RecorderLike;
  isTypeSupported(type: string): boolean;
}

export interface PieceSink {
  push(piece: QueuedPiece): boolean;
}

export interface RecorderDeps {
  stream: MediaStream;
  Recorder: RecorderCtor | undefined;
  sink: PieceSink;
  /** Monotonic clock in ms (performance.now in the page). */
  now: () => number;
  /** server_now - started_at from join_huddle (plan 2.3). */
  clockBaseMs: number;
  /** now() at the moment join returned. */
  t0: number;
  onState: (state: RecordingState) => void;
  pieceMs?: number;
}

/** First format of the preference list this browser can record (plan 2.2). */
export function pickRecorderMime(Recorder: RecorderCtor | undefined): string | null {
  if (Recorder === undefined || typeof Recorder.isTypeSupported !== 'function') return null;
  for (const type of RECORDER_MIME_PREFERENCE) {
    try {
      if (Recorder.isTypeSupported(type)) return type;
    } catch {
      // A browser that throws on the question cannot answer it.
    }
  }
  return null;
}

interface Piece {
  recorder: RecorderLike;
  parts: Blob[];
  startedAt: number;
  /** Set when stop() is called on it: the piece ends there, not when onstop fires. */
  endedAt: number | null;
  /** False only when the person was muted from the first to the last moment (2.5). */
  heard: boolean;
  done: Promise<void>;
}

export class HuddleRecorder {
  private readonly deps: RecorderDeps;
  private readonly askedMime: string | null;
  private current: Piece | null = null;
  private readonly closing = new Set<Promise<void>>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private seq = 0;
  private stopped = false;
  private notRecorded = false;
  private muted = false;

  constructor(deps: RecorderDeps) {
    this.deps = deps;
    this.askedMime = pickRecorderMime(deps.Recorder);
  }

  get isRecording(): boolean {
    return this.current !== null && !this.stopped;
  }

  /** Starts the first piece; says 'not_recorded' at once when it cannot. */
  start(): void {
    if (this.stopped || this.current !== null) return;
    if (this.askedMime === null || this.deps.Recorder === undefined) {
      this.markNotRecorded();
      return;
    }
    this.startPiece();
  }

  /** Mute keeps recording (silence); a piece muted the whole way is not sent (2.5). */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!muted && this.current !== null) this.current.heard = true;
  }

  /** Stops for good; resolves once every open piece has been handed to the queue. */
  async stop(): Promise<void> {
    this.stopped = true;
    this.clearTimer();
    this.closePiece();
    await Promise.all([...this.closing]);
  }

  /** The uploader was refused: nothing more is made for this seat (plan 2.4). */
  refused(): void {
    this.markNotRecorded();
  }

  private clearTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private markNotRecorded(): void {
    if (this.notRecorded) return;
    this.notRecorded = true;
    this.stopped = true;
    this.clearTimer();
    const piece = this.current;
    this.current = null;
    try {
      if (piece !== null && piece.recorder.state !== 'inactive') piece.recorder.stop();
    } catch {
      // Already stopped.
    }
    this.deps.onState('not_recorded');
  }

  private startPiece(): void {
    const Recorder = this.deps.Recorder;
    if (Recorder === undefined || this.askedMime === null) return;
    let recorder: RecorderLike;
    try {
      recorder = new Recorder(this.deps.stream, {
        mimeType: this.askedMime,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch {
      this.markNotRecorded();
      return;
    }
    let resolveDone: () => void = () => undefined;
    const piece: Piece = {
      recorder,
      parts: [],
      startedAt: this.deps.now(),
      endedAt: null,
      heard: !this.muted,
      done: new Promise<void>((resolve) => {
        resolveDone = resolve;
      }),
    };
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) piece.parts.push(event.data);
    };
    recorder.onerror = () => {
      this.markNotRecorded();
      resolveDone();
    };
    recorder.onstop = () => {
      this.handlePiece(piece);
      resolveDone();
    };
    try {
      recorder.start();
    } catch {
      this.markNotRecorded();
      return;
    }
    this.current = piece;
    this.timer = setTimeout(() => {
      this.rotate();
    }, this.deps.pieceMs ?? PIECE_MS);
  }

  /** Stop this piece, then start the next one on the same stream. */
  private rotate(): void {
    if (this.stopped) return;
    this.closePiece();
    this.startPiece();
  }

  private closePiece(): void {
    const piece = this.current;
    this.current = null;
    if (piece === null || piece.recorder.state === 'inactive') return;
    piece.endedAt = this.deps.now();
    try {
      piece.recorder.stop();
    } catch {
      return;
    }
    const done = piece.done;
    this.closing.add(done);
    void done.then(() => this.closing.delete(done));
  }

  private handlePiece(piece: Piece): void {
    if (this.notRecorded) return;
    const type = piece.parts[0]?.type || piece.recorder.mimeType || this.askedMime || '';
    const mime: BareAudioMime | null = acceptedMime(type);
    if (mime === null) {
      this.markNotRecorded();
      return;
    }
    if (!piece.heard) return; // muted for the whole piece (2.5)
    const blob = new Blob(piece.parts, { type: mime });
    if (blob.size === 0 || blob.size > PIECE_MAX_BYTES) return; // 2.8
    const endedAt = piece.endedAt ?? this.deps.now();
    const durationMs = Math.max(1, Math.min(35_000, Math.round(endedAt - piece.startedAt)));
    const offsetMs = Math.max(
      0,
      Math.round(this.deps.clockBaseMs + (piece.startedAt - this.deps.t0)),
    );
    const accepted = this.deps.sink.push({
      seq: this.seq,
      offsetMs,
      durationMs,
      blob,
      mime,
      finishedAt: endedAt,
    });
    if (accepted) this.seq += 1;
  }
}

/**
 * Leave (plan 2.7): stop the recorder, wait for the last piece to upload for
 * up to 10 s ("Saving your audio..." on the button), then leave the seat.
 * leave always runs, even when the wait times out or the recorder throws.
 */
export async function saveThenLeave(opts: {
  recorder: { stop: () => Promise<void> } | null;
  queue: { whenIdle: () => Promise<void>; stop: () => void } | null;
  leave: () => Promise<void>;
  sleep: (ms: number) => Promise<void>;
  waitMs?: number;
}): Promise<void> {
  try {
    await Promise.race([
      (async () => {
        await opts.recorder?.stop();
        await opts.queue?.whenIdle();
      })(),
      opts.sleep(opts.waitMs ?? LEAVE_WAIT_MS),
    ]);
  } catch {
    // Whatever was not saved is lost (design-note.md:31); the seat still goes.
  } finally {
    opts.queue?.stop();
  }
  await opts.leave();
}
